import { RootState } from "../../app";
import { createSelector } from "@reduxjs/toolkit";
import { Annotation } from "../../annotator/interfaces/annotation";
import { documentSetApi } from "../../documentSet/store/documentSetApi";
import { buildAnnotationView } from "../utils/buildAnnotationView";

/**
 * The annotation version the toggle shows next to the current annotations: the state at
 * export. Later edit rounds are pinned under "1", "2", ... in the same map.
 */
export const EXPORTED_VERSION = 0;

const selectActiveDocumentSet = (state: RootState) =>
  state.documentSetsReducer.activeDocumentSet;

const selectConfigMap = (state: RootState) => state.configMapReducer.configMap;

const selectCategorizationAnnotations = (state: RootState) =>
  state.metadataReducer.categorizationState.categorizationAnnotations;

/**
 * There is something to toggle between once a round of edits has been confirmed. Starting an
 * edit session pins the exported revision, but until it is confirmed both views would show
 * the same annotations.
 */
export const selectHasSnapshot = (state: RootState): boolean =>
  (selectActiveDocumentSet(state)?.annotationVersion ?? 0) > 0;

export const selectIsShowingSnapshot = (state: RootState): boolean =>
  state.appReducer.annotationsViewMode === "exported" &&
  selectHasSnapshot(state);

const selectSnapshot = (state: RootState) => {
  const documentSetId = selectActiveDocumentSet(state)?.id;
  if (!documentSetId) {
    return undefined;
  }

  return documentSetApi.endpoints.getAnnotationSnapshot.select({
    documentSetId,
    version: EXPORTED_VERSION,
  })(state).data;
};

// Built with the same pipeline as the annotations a document set loads with, so rows,
// indices and group blocks line up between both views. Categorizations are not part of a
// snapshot — the current ones are fed in.
export const selectSnapshotView = createSelector(
  selectSnapshot,
  selectCategorizationAnnotations,
  selectConfigMap,
  (snapshot, categorizationAnnotations, configMap) => {
    if (!snapshot || !configMap) {
      return undefined;
    }

    return buildAnnotationView(
      snapshot.documents.flatMap(
        (document) => document.entityAnnotations ?? [],
      ),
      categorizationAnnotations,
      configMap,
    );
  },
);

/**
 * The annotations the detail page works with: the exported snapshot while the toggle is on
 * it, the editable ones otherwise. Falls back to the editable ones while a snapshot is
 * still loading or failed to load, so the document is never rendered as empty.
 */
const selectAnnotationReducer = createSelector(
  (state: RootState) => state.annotationReducer.tableControlAnnotations,
  (state: RootState) => state.annotationReducer.sideControlAnnotations,
  selectIsShowingSnapshot,
  selectSnapshotView,
  (
    tableControlAnnotations,
    sideControlAnnotations,
    isShowingSnapshot,
    snapshotView,
  ): Array<Annotation> =>
    isShowingSnapshot && snapshotView
      ? snapshotView.annotations
      : [...tableControlAnnotations, ...sideControlAnnotations],
);

export default selectAnnotationReducer;
